import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { GOOGLE_CLIENT_ID, loadGoogleIdentityScript } from '../../lib/googleIdentity'

// Google's rendered button doesn't support a width beyond this, no
// matter what's requested — rendered once at this size, then scaled
// horizontally (see below) to actually fill the container.
const GOOGLE_BUTTON_WIDTH = 400

/**
 * Renders Google's own "Sign in with Google" button (via Identity
 * Services' `renderButton`) rather than a custom one — guarantees
 * correct branding and handles the whole popup/credential flow
 * internally. `onCredential` receives the raw Google ID token JWT;
 * the caller sends it to `POST /api/v1/auth/google`.
 */
export function GoogleSignInButton({ onCredential, disabled }: {
  onCredential: (idToken: string) => void
  disabled?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mountRef = useRef<HTMLDivElement>(null)
  // Keep the latest callback in a ref so the init effect (which should
  // only run once) doesn't need `onCredential` in its dependency array.
  const onCredentialRef = useRef(onCredential)
  onCredentialRef.current = onCredential

  useEffect(() => {
    let cancelled = false
    let observer: ResizeObserver | null = null
    loadGoogleIdentityScript()
      .then(() => {
        if (cancelled || !mountRef.current || !containerRef.current || !window.google) return
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => onCredentialRef.current(response.credential),
        })
        window.google.accounts.id.renderButton(mountRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          width: GOOGLE_BUTTON_WIDTH,
        })

        // Stretch the rendered widget horizontally to match the
        // container (and the Log In button next to it) — height/
        // border-radius are untouched since this only scales X.
        const scale = () => {
          if (!mountRef.current || !containerRef.current) return
          const factor = containerRef.current.clientWidth / GOOGLE_BUTTON_WIDTH
          mountRef.current.style.transform = `scaleX(${factor})`
        }
        scale()
        observer = new ResizeObserver(scale)
        observer.observe(containerRef.current)
      })
      .catch(() => {
        // Script failed to load (offline, blocked) — the button area
        // just stays empty rather than showing a broken widget.
      })
    return () => {
      cancelled = true
      observer?.disconnect()
    }
  }, [])

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <Box ref={mountRef} sx={{ width: `${GOOGLE_BUTTON_WIDTH}px`, transformOrigin: 'left center' }} />
    </Box>
  )
}
